// src/utils/navigationHelpers.js

/**
 * Navigation helper functions for common routing issues
 */

/**
 * Navigate to sign-in page with proper error handling
 * @param {Function} navigate - React Router navigate function
 * @param {Object} options - Navigation options
 */
export const navigateToSignIn = (navigate, options = {}) => {
  const { 
    mode = 'signin', 
    returnUrl = null, 
    expired = false,
    replace = false 
  } = options;

  try {
    // Build the auth URL with parameters
    const searchParams = new URLSearchParams();
    if (mode) searchParams.set('mode', mode);
    if (expired) searchParams.set('expired', 'true');
    
    const authUrl = `/auth${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    
    // Navigate with state for return URL
    const navigationState = returnUrl ? { from: { pathname: returnUrl } } : undefined;
    
    navigate(authUrl, { 
      replace,
      state: navigationState 
    });
    
    console.log('✅ Navigated to sign-in:', authUrl);
  } catch (error) {
    console.error('❌ Navigation to sign-in failed:', error);
    
    // Fallback: direct window navigation
    try {
      const fallbackUrl = `/auth?mode=${mode}${expired ? '&expired=true' : ''}`;
      window.location.href = fallbackUrl;
    } catch (fallbackError) {
      console.error('❌ Fallback navigation also failed:', fallbackError);
      alert('Unable to navigate to sign-in page. Please refresh the page and try again.');
    }
  }
};

/**
 * Navigate to sign-up page
 * @param {Function} navigate - React Router navigate function
 * @param {Object} options - Navigation options
 */
export const navigateToSignUp = (navigate, options = {}) => {
  navigateToSignIn(navigate, { ...options, mode: 'signup' });
};

/**
 * Check if current path is an auth page
 * @param {string} pathname - Current pathname
 * @returns {boolean}
 */
export const isAuthPage = (pathname) => {
  return pathname === '/auth' || pathname === '/forgot-password';
};

/**
 * Get return URL from current location
 * @param {Object} location - React Router location object
 * @returns {string}
 */
export const getReturnUrl = (location) => {
  // Don't return to auth pages
  if (isAuthPage(location.pathname)) {
    return '/movies';
  }
  
  return location.pathname + location.search;
};

/**
 * Force navigation with page reload as fallback
 * @param {string} url - URL to navigate to
 */
export const forceNavigate = (url) => {
  try {
    // Try React Router first
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else {
      // Fallback to direct navigation
      window.location.href = url;
    }
  } catch (error) {
    console.error('Force navigation failed:', error);
    window.location.href = url;
  }
};

/**
 * Debug current navigation state
 * @param {Object} location - React Router location object
 * @param {Object} user - Current user object
 * @returns {Object} Debug information
 */
export const debugNavigation = (location, user) => {
  return {
    currentPath: location.pathname,
    currentSearch: location.search,
    currentState: location.state,
    isAuthenticated: !!user,
    userEmail: user?.email || 'Not available',
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    referrer: document.referrer
  };
};